// apps/app/src/features/auth/reset.page.tsx
//
// The request form always reports success, whether or not the account exists —
// otherwise this endpoint is an account-enumeration oracle open to the internet.

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CircleCheck, KeyRound, LockKeyhole, Mail, MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { http } from '@/lib/api-client';
import { AuthField, AuthHeading, AuthLayout, IconInput, PasswordInput } from './auth-layout';

export function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <SetNewPassword token={token} /> : <RequestReset />;
}

function RequestReset() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    // Deliberately ignores failures: the response must be identical either way.
    await http.post('/auth/forgot-password', { email }).catch(() => undefined);
    setSent(true);
    setBusy(false);
  };

  if (sent) {
    return (
      <AuthLayout>
        <AuthHeading icon={MailCheck} title={t('reset.sentTitle')} subtitle={t('reset.sentBody', { email })} />
        <BackToSignIn label={t('reset.backToSignIn')} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading icon={KeyRound} title={t('reset.title')} subtitle={t('reset.subtitle')} />
      <form onSubmit={submit} className="space-y-5">
        <AuthField id="reset-email" label={t('fields.email')}>
          <IconInput
            id="reset-email"
            icon={Mail}
            type="email"
            required
            autoComplete="email"
            placeholder={t('placeholders.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </AuthField>

        <Button loading={busy} className="h-11 w-full">
          {t('reset.submit')}
        </Button>

        <BackToSignIn label={t('reset.backToSignIn')} />
      </form>
    </AuthLayout>
  );
}

function SetNewPassword({ token }: { token: string }) {
  const { t } = useTranslation('auth');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch {
      setError(t('reset.linkExpired'));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        {/* Every other session was signed out. If the reset was driven by a
            compromise, leaving them alive defeats the point. */}
        <AuthHeading icon={CircleCheck} title={t('reset.doneTitle')} subtitle={t('reset.doneBody')} />
        <Button asChild className="h-11 w-full">
          <Link to="/login">{t('reset.signIn')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading icon={KeyRound} title={t('reset.newTitle')} subtitle={t('reset.newSubtitle')} />
      <form onSubmit={submit} className="space-y-5">
        <AuthField id="reset-password" label={t('fields.newPassword')} error={error ?? undefined} hint={t('password.minLengthHint')}>
          <PasswordInput
            id="reset-password"
            icon={LockKeyhole}
            required
            minLength={12}
            autoComplete="new-password"
            placeholder={t('placeholders.newPassword')}
            aria-describedby={error ? 'reset-password-error' : 'reset-password-hint'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </AuthField>

        <Button loading={busy} className="h-11 w-full">
          {t('reset.change')}
        </Button>

        <BackToSignIn label={t('reset.backToSignIn')} />
      </form>
    </AuthLayout>
  );
}

function BackToSignIn({ label }: { label: string }) {
  return (
    <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" aria-hidden="true" />
      {label}
    </Link>
  );
}
